import { useState } from "react";
import { Typography, Box, useTheme, Skeleton, Alert, Dialog, DialogContent, Stack } from "@mui/material";
import { Button, FoodsIcon, FoodsIllustrations, FoodsTags, WhiteBox } from "@foodpilot/foods";
import { useGetTrendingTopics } from "../../../../api/client-api/social/useTopics";
import { AllTopics } from "./AllTopics";
import { TopicHashtagLabel } from "./TopicHashtagLabel";
import { filterUserTopics } from "../../utils/postType";
import { useTranslation } from "react-i18next";

interface CommunityTopicsProps {
    activeTopicId?: number | null;
    onTopicClick?: (topicId: number, topicName: string) => void;
}

const CommunityTopicsHeader = ({ title }: { title: string }) => (
    <Stack direction="row" alignItems="center" gap={1}>
        <FoodsIcon icon="tag" size={2} />
        <Typography variant="h6">{title}</Typography>
    </Stack>
);

const useCommunityTopicsData = () => useGetTrendingTopics("trending", 8);

export const CommunityTopicsStrip = ({ activeTopicId, onTopicClick }: CommunityTopicsProps = {}) => {
    const { data, isLoading } = useCommunityTopicsData();
    const userTopics = filterUserTopics(data ?? []);

    if (isLoading || !userTopics.length) {
        return null;
    }

    return (
        <Stack
            direction="row"
            gap={1}
            sx={{
                overflowX: "auto",
                pb: 0.5,
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
            }}
        >
            {userTopics.map((topic) => (
                <FoodsTags
                    key={topic.id}
                    label={`#${topic.name}`}
                    startIcon="tag"
                    variant={activeTopicId === topic.id ? "tags-coral" : "tags-white"}
                    onClick={() => onTopicClick?.(topic.id, topic.name)}
                    sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                />
            ))}
        </Stack>
    );
};

export const CommunityTopics = ({ activeTopicId, onTopicClick }: CommunityTopicsProps = {}) => {
    const theme = useTheme();
    const [allTopicsOpen, setAllTopicsOpen] = useState(false);
    const { data, isLoading, isError } = useCommunityTopicsData();
    const { t } = useTranslation();

    const title = t("social.topics.community_title", "Community hashtags");
    const userTopics = filterUserTopics(data ?? []);
    const hasOnlyTypeTopics = !isLoading && !isError && (data?.length ?? 0) > 0 && userTopics.length === 0;

    const allTopicsDialog = (
        <Dialog
            open={allTopicsOpen}
            onClose={() => setAllTopicsOpen(false)}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { minHeight: "70vh" } }}
        >
            <DialogContent sx={{ p: 0 }}>
                <AllTopics
                    onTopicClick={(topicId, topicName) => {
                        onTopicClick?.(topicId, topicName);
                        setAllTopicsOpen(false);
                    }}
                    onClose={() => setAllTopicsOpen(false)}
                />
            </DialogContent>
        </Dialog>
    );

    if (isLoading) {
        return (
            <WhiteBox sx={{ p: 2 }}>
                <Stack gap={2}>
                    <CommunityTopicsHeader title={title} />
                    <Stack gap={1}>
                        {Array.from({ length: 4 }).map((_, index) => (
                            <Stack key={index} direction="row" alignItems="center" gap={2}>
                                <Box flex={1}>
                                    <Skeleton variant="text" width="60%" height={20} />
                                    <Skeleton variant="text" width="40%" height={16} />
                                </Box>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            </WhiteBox>
        );
    }

    if (isError) {
        return (
            <WhiteBox sx={{ p: 2 }}>
                <Stack gap={2}>
                    <CommunityTopicsHeader title={title} />
                    <Alert severity="info">{t("social.topics.error.load", "Unable to load trending topics")}</Alert>
                </Stack>
            </WhiteBox>
        );
    }

    if (!userTopics.length) {
        return (
            <>
                <WhiteBox sx={{ p: 2 }}>
                    <Stack gap={2}>
                        <CommunityTopicsHeader title={title} />
                        <Stack alignItems="center" gap={1} py={1} textAlign="center">
                            <FoodsIllustrations illustration="communities" width={56} height={56} />
                            <Typography variant="body2" color="text.secondary">
                                {hasOnlyTypeTopics ?
                                    t(
                                        "social.topics.community_empty_hint",
                                        "Add hashtags like #packaging to your posts to build community topics here.",
                                    )
                                :   t(
                                        "social.topics.empty",
                                        "Popular topics will appear once the community starts posting.",
                                    )
                                }
                            </Typography>
                        </Stack>
                        <Button variant="text" onClick={() => setAllTopicsOpen(true)}>
                            {t("social.topics.browse_all", "Browse all topics")}
                        </Button>
                    </Stack>
                </WhiteBox>
                {allTopicsDialog}
            </>
        );
    }

    return (
        <>
            <WhiteBox sx={{ p: 2 }}>
                <Stack gap={2}>
                    <CommunityTopicsHeader title={title} />
                    <Typography variant="caption" color="text.secondary">
                        {t("social.topics.community_hint", "Filter the feed by business topic.")}
                    </Typography>
                    <Stack gap={0.5}>
                        {userTopics.map((topic) => {
                            const selected = activeTopicId === topic.id;

                            return (
                                <Stack
                                    key={topic.id}
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                    onClick={() => onTopicClick?.(topic.id, topic.name)}
                                    sx={{
                                        cursor: "pointer",
                                        p: 1,
                                        borderRadius: 2,
                                        backgroundColor: selected ? theme.palette.primary[50] : "transparent",
                                        border:
                                            selected ?
                                                `1px solid ${theme.palette.primary[200]}`
                                            :   "1px solid transparent",
                                        transition: "background-color 0.15s ease, transform 0.15s ease",
                                        "&:hover": {
                                            backgroundColor: theme.palette.primary[50],
                                            transform: "translateX(4px)",
                                        },
                                    }}
                                >
                                    <Stack gap={0.5} minWidth={0} flex={1}>
                                        <TopicHashtagLabel name={topic.name} truncate />
                                        <Typography variant="caption" color="text.secondary">
                                            {topic.postCount}{" "}
                                            {topic.postCount === 1 ?
                                                t("social.topics.post", "post")
                                            :   t("social.topics.posts", "posts")}{" "}
                                            {t("social.topics.this_week", "this week")}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            );
                        })}
                    </Stack>
                    <Button variant="text" onClick={() => setAllTopicsOpen(true)}>
                        {t("social.topics.browse_all", "Browse all topics")}
                    </Button>
                </Stack>
            </WhiteBox>
            {allTopicsDialog}
        </>
    );
};
